import type { CaptureResult, PostHogConfig } from "posthog-js";

const credentialParameters = [
  "token",
  "token_hash",
  "access_token",
  "refresh_token",
  "code",
];

function sanitizeValue(value: unknown): unknown {
  if (typeof value === "string" && /^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      for (const key of [...url.searchParams.keys()]) {
        if (credentialParameters.includes(key.toLowerCase())) {
          url.searchParams.delete(key);
        }
      }
      // Supabase can also return access and refresh tokens in the fragment.
      url.hash = "";
      return url.toString();
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (value && typeof value === "object") {
    return sanitizeProperties(value);
  }
  return value;
}

function sanitizeProperties(properties: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      key,
      sanitizeValue(value),
    ]),
  );
}

function sanitizeEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null;
  return {
    ...event,
    properties: sanitizeProperties(event.properties),
    ...(event.$set && { $set: sanitizeProperties(event.$set) }),
    ...(event.$set_once && { $set_once: sanitizeProperties(event.$set_once) }),
  };
}

export const posthogPrivacyConfig = {
  // Mask at collection time as well as at the outgoing event boundary.
  mask_personal_data_properties: true,
  custom_personal_data_properties: credentialParameters,
  disable_capture_url_hashes: true,
  before_send: sanitizeEvent,
} satisfies Partial<PostHogConfig>;
